import { useState } from "react";
import { motion } from "framer-motion";
import { Swords, Send, Trophy, ArrowRight, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import ConsentGate from "@/components/ConsentGate";
import Waveform from "@/components/Waveform";
import BundleHint from "@/components/BundleHint";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { useAuth } from "@/context/AuthContext";
import { voiceOK, langDisplayName } from "@/lib/languages";
import { useI18n } from "@/context/I18nContext";
import { dimLabel, sideLabel } from "@/lib/practiceLabels";

export default function Debate() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [showMeaning, setShowMeaning] = useState(false);
  const [debate, setDebate] = useState(null);
  const [topicInput, setTopicInput] = useState("");
  const [rounds, setRounds] = useState([]);
  const [argument, setArgument] = useState("");
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [consent, setConsent] = useState(false);
  const { recording, analyser, start: startRec, stop: stopRec } = useAudioRecorder();

  const start = async () => {
    setLoading(true);
    try {
      const res = await api.post("/debate/start", { topic: topicInput || null });
      setDebate(res.data);
      setShowMeaning(false);
      setRounds([{ id: "opening", role: "ai", text: res.data.opening }]);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else if (e?.response?.data?.detail?.code === "debate_topic_failed")
        toast.error(t("pm.debate.topicFailed", { lang: langDisplayName(e.response.data.detail.lang, locale) }));
      else toast.error(t("pm.debate.startFailed"));
    } finally { setLoading(false); }
  };

  const runRound = async (payload, spokenPlaceholder) => {
    setRounds((r) => [...r, { id: `u-${Date.now()}`, role: "user", text: spokenPlaceholder }]);
    setLoading(true);
    try {
      const res = await api.post("/debate/round", { debate_id: debate.debate_id, ...payload });
      setRounds((r) => {
        const copy = [...r];
        // fill in the transcribed argument for voice turns
        if (res.data.you_said) copy[copy.length - 1] = { ...copy[copy.length - 1], text: res.data.you_said };
        return [...copy, {
          id: `a-${Date.now()}`,
          role: "ai", text: res.data.rebuttal, translation: res.data.rebuttal_translation,
          scores: res.data.scores, weakest: res.data.weakest, improvement: res.data.improvement,
        }];
      });
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(t("pm.debate.roundFailed"));
      setRounds((r) => r.slice(0, -1));
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!argument.trim() || loading) return;
    const arg = argument.trim();
    setArgument("");
    runRound({ argument: arg }, arg);
  };

  const startVoice = async () => {
    if (!localStorage.getItem("fluent_consent_mic")) { setConsent(true); return; }
    try { await startRec(); } catch { toast.error(t("pm.common.micUnavailable")); }
  };

  const stopVoice = async () => {
    const b64 = await stopRec();
    if (b64) runRound({ audio: b64, mime: "audio/webm" }, t("pm.debate.transcribing"));
    else toast.error(t("pm.debate.noAudio"));
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">DebateGym</h1>
        <p className="text-gray-600">{t("pm.debate.subtitle")}</p>
        <BundleHint id="debate">{t("pm.debate.hintPre")} <b>{t("pm.debate.hintVoice")}</b> {t("pm.debate.hintPost")}</BundleHint>

        {!debate ? (
          <div className="nb-card bg-sky p-6 mt-6">
            <Swords className="w-10 h-10" />
            <h3 className="font-cabinet font-bold text-xl mt-3">{t("pm.debate.startTitle")}</h3>
            <input className="nb-input mt-4" placeholder={t("pm.debate.topicPlaceholder")} value={topicInput} onChange={(e) => setTopicInput(e.target.value)} data-testid="debate-topic-input" />
            <button onClick={start} disabled={loading} className="nb-btn-primary w-full mt-4 py-3" data-testid="debate-start-btn">
              {loading ? t("pm.debate.settingUp") : t("pm.debate.begin")} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
            </button>
          </div>
        ) : (
          <>
            <div className="nb-card bg-butter p-5 mt-6">
              <div className="nb-tag bg-white">{t("pm.debate.topic")}</div>
              <p className="font-cabinet font-bold text-lg mt-2" dir="auto" data-testid="debate-topic">{debate.topic_target || debate.topic}</p>
              {debate.topic_target && debate.topic && debate.topic !== debate.topic_target && (
                <div className="mb-1">
                  <button onClick={() => setShowMeaning((v) => !v)} className="text-xs font-bold underline underline-offset-2" data-testid="debate-show-meaning">
                    {showMeaning ? t("pm.debate.hideMeaning") : t("pm.debate.showMeaning")}</button>
                  {showMeaning && <p className="text-sm text-gray-600 mt-1" dir="auto" data-testid="debate-topic-meaning">{debate.topic}</p>}
                </div>
              )}
              <p className="text-sm">{t("pm.debate.mustArgue")} <span className="font-bold uppercase" data-testid="debate-user-side">{sideLabel(t, debate.user_side)}</span></p>
            </div>

            <div className="space-y-4 mt-6">
              {rounds.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <div className={`nb-card p-4 ${r.role === "user" ? "bg-lavender ms-8" : "bg-white me-8"}`}>
                    <div className="text-xs font-bold uppercase tracking-widest mb-1">{r.role === "user" ? t("pm.debate.you") : t("pm.debate.opponent")}</div>
                    <p className="font-medium" dir="auto">{r.text}</p>
                    {r.translation && <p className="text-xs text-gray-500 mt-1" dir="auto">{r.translation}</p>}
                  </div>
                  {r.scores && (
                    <div className="nb-card bg-mint p-4 mt-2 me-8" data-testid="debate-scores">
                      <div className="flex items-center gap-2 font-cabinet font-bold"><Trophy className="w-5 h-5" /> {t("pm.debate.roundScores")}</div>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {Object.entries(r.scores).map(([k, v]) => (
                          <span key={k} className="nb-tag bg-white">{dimLabel(t, k)}: {v}</span>
                        ))}
                      </div>
                      {r.improvement && <p className="text-sm mt-2"><strong>{t("pm.debate.improve")}</strong> <span dir="auto">{r.improvement}</span></p>}
                    </div>
                  )}
                </motion.div>
              ))}
              {loading && <p className="text-center text-gray-400 font-bold">{t("pm.debate.thinking")}</p>}
            </div>

            {recording && (
              <div className="nb-card bg-sky p-4 mt-4 flex items-center gap-4" data-testid="debate-waveform">
                <span className="nb-tag bg-white shrink-0">● REC</span>
                <div className="flex-1"><Waveform analyser={analyser} active color="#0A0A0A" height={56} bars={36} /></div>
              </div>
            )}

            <div className="flex gap-2 mt-5 sticky bottom-4">
              <input className="nb-input flex-1" placeholder={t("pm.debate.argPlaceholder")} value={argument} onChange={(e) => setArgument(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} disabled={recording} data-testid="debate-argument-input" />
              {recording ? (
                <button onClick={stopVoice} className="nb-btn-primary px-5 animate-pulse" data-testid="debate-stop-voice-btn"><Square className="w-5 h-5 fill-white" /></button>
              ) : !voiceOK(user?.target_lang) ? null : (
                <button onClick={startVoice} disabled={loading} className="nb-btn-mint px-5" data-testid="debate-voice-btn" title={t("pm.debate.voiceTitle")} aria-label={t("pm.debate.voiceTitle")}><Mic className="w-5 h-5" /></button>
              )}
              <button onClick={submit} disabled={loading || recording} className="nb-btn-primary px-5" data-testid="debate-submit-btn"><Send className="w-5 h-5" /></button>
            </div>
          </>
        )}
      </div>
      <ConsentGate type="mic" open={consent} onGrant={() => { setConsent(false); startVoice(); }} onClose={() => setConsent(false)} />
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("pm.common.proFeature", { name: "DebateGym" })} />
    </AppLayout>
  );
}
